import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { AgentTypes, EventTypes } from '../constants/event';
import { PromotedTextRef } from '../reducers/messagingSlice';
import { selectFeatureFlagSecondaryAsrEnabled } from '../redux/features/config/config.selector';
import { ISOStringToEpoch } from '../utils/network';

export function selectHypothesisFrame(state: RootState) {
  return state.messages.hypothesisFrame;
}

const selectMessages = (state: RootState) => state.messages;
const selectIsAIActive = createSelector(
  selectMessages,
  (messages) => messages.isAIActive
);
const selectIsWebSocketConnected = createSelector(
  selectMessages,
  (messages) => messages.isConnected
);
const selectIsNewSession = createSelector(
  selectMessages,
  (messages) => messages.isNewSession
);
const selectRestaurantStaffIntervention = createSelector(
  selectMessages,
  (messages) => messages.restaurantStaffIntervention
);
const selectIsStaffIntervention = createSelector(
  selectMessages,
  (messages) => messages.isStaffIntervention
);
const selectIsPlaying = createSelector(
  selectMessages,
  (messages) => messages.isPlaying
);
const selectCurrentSessionId = createSelector(
  selectMessages,
  (messages) => messages.currentSessionId
);
const selectIsStaffIntervenedBeforeCarExit = createSelector(
  selectMessages,
  (messages) => messages.isStaffIntervenedBeforeCarExit
);

const selectFinalTextFrames = createSelector(
  selectMessages,
  (messages) => messages.finalTextFrames
);
const selectInProgressHypothesisTextFrames = createSelector(
  selectMessages,
  (messages) => messages.inProgressHypothesisTextFrames
);
const selectSentTextFrames = createSelector(
  selectMessages,
  (messages) => messages.sentTextFrames
);

const selectSBFinalTextFrames = createSelector(
  selectMessages,
  (messages) => messages.sbFinalTextFrames
);
const selectSBInProgressHypothesisTextFrames = createSelector(
  selectMessages,
  (messages) => messages.sbInProgressHypothesisTextFrames
);
const selectIsCarPresent = createSelector(
  selectMessages,
  (messages) => messages.isCarPresent
);

const selectEntityAnnotationsMap = createSelector(
  selectMessages,
  (messages) => messages.entityAnnotationsMap
);

// secondary_asr (HITL-1497): recorded promoted-text occurrences. With
// `secondary_asr_enabled` on these become the customer transcript lines; with it
// off nothing reads them.
const selectPromotedTextEvents = createSelector(
  selectMessages,
  (messages) => messages.promotedTextEvents
);
const selectSBPromotedTextEvents = createSelector(
  selectMessages,
  (messages) => messages.sbPromotedTextEvents
);

/**
 * HITL 3.0 — anchor for the Pulse "Response time" tile.
 *
 * Returns the epoch (ms) of the customer's most recent utterance IF the agent
 * has not yet replied to it, otherwise `null` (idle). Both the customer's final
 * ASR and the agent's spoken reply arrive as ORDERBOARD text frames in
 * `finalTextFrames`; they're told apart by `data.status` — a `TTS` frame is the
 * agent's reply, anything else is the customer (mirrors MessageContainer).
 *
 * The tile counts up from this anchor; the moment a TTS lands after the latest
 * customer utterance, this goes `null` and the tile resets to 0s / empty.
 */
/**
 * HITL 3.0 — the customer utterance currently awaiting an agent reply.
 *
 * Returns `{ sinceMs, text }` for the customer's most recent utterance IF the
 * agent has not yet replied to it, otherwise `null` (idle). Customer ASR and the
 * agent's spoken reply both arrive as ORDERBOARD text frames in
 * `finalTextFrames`; they're told apart by `data.status` — a `TTS` frame is the
 * agent's reply, anything else is the customer (mirrors MessageContainer).
 */
const selectAwaitingCustomerUtterance = createSelector(
  selectFinalTextFrames,
  (frames): { sinceMs: number; text: string } | null => {
    let lastCustomerMs: number | null = null;
    let lastCustomerText = '';
    let lastTtsMs: number | null = null;

    frames.forEach((frame) => {
      const ts = ISOStringToEpoch(frame?.timestamp);
      if (Number.isNaN(ts)) return;
      // Only ORDERBOARD frames matter (mirrors MessageContainer, which gates on
      // agent_type before the TTS/non-TTS split). TTS frames from other agents
      // (HITL, RESTAURANTSTAFF) also land in finalTextFrames and must NOT count as
      // "the agent replied", or they'd falsely reset the awaiting state to idle.
      if (frame?.agent_type !== AgentTypes.orderBoard) return;
      if (frame?.data?.status === 'TTS') {
        if (lastTtsMs === null || ts > lastTtsMs) lastTtsMs = ts;
        return;
      }
      // HITL-1496: an empty final is not a customer utterance. Stores running
      // `flush_on_tts` blank the recognizer on every agent TTS, and the flush
      // emits an ORDERBOARD final with an empty payload ~0.2-0.6s later. Those
      // frames render nothing (MessageContainer gates on a truthy payload), so
      // arming on one starts the clock against a transcript whose newest line is
      // the agent's own reply — the meter climbs, and the agent_response_time
      // metric can fire EXCEEDED with `customer_utterance: ""`.
      const payload = frame?.data?.payload;
      if (typeof payload !== 'string' || payload.trim() === '') return;
      if (lastCustomerMs === null || ts > lastCustomerMs) {
        lastCustomerMs = ts;
        lastCustomerText = payload;
      }
    });

    if (lastCustomerMs === null) return null;
    // Agent has already replied to (or after) the latest utterance → idle.
    if (lastTtsMs !== null && lastTtsMs >= lastCustomerMs) return null;
    return { sinceMs: lastCustomerMs, text: lastCustomerText };
  }
);

/**
 * Epoch (ms) anchor for the Pulse "Response time" tile — the awaiting utterance's
 * timestamp, or `null` when idle. Derived from selectAwaitingCustomerUtterance so
 * the tile's clock and the response-time metric stay in lockstep.
 */
const selectAwaitingResponseSinceMs = createSelector(
  selectAwaitingCustomerUtterance,
  (awaiting): number | null => (awaiting ? awaiting.sinceMs : null)
);

/**
 * WS-3 (HITL-1411): merge + sort transcript frames once.
 *
 * Sorts on a precomputed epoch — one `Date` parse per frame (O(n)) instead of
 * `new Date()` twice per comparison (O(n log n)) — then appends the most recent
 * in-progress hypothesis, if any, after the sorted final/sent frames. This
 * matches the previous in-hook behavior exactly; only *where/when* it runs
 * changes (memoized in the selector vs. rebuilt on every render).
 */
const buildSortedTranscript = (
  textFrames: any[],
  hypothesisFrames: any[]
): any[] => {
  const sorted = textFrames
    .map((frame) => ({ frame, epoch: ISOStringToEpoch(frame?.timestamp) }))
    .sort((a, b) => a.epoch - b.epoch)
    .map((entry) => entry.frame);

  if (hypothesisFrames.length) {
    sorted.push(hypothesisFrames[hypothesisFrames.length - 1]);
  }
  return sorted;
};

/**
 * secondary_asr (HITL-1497): is this a committed CUSTOMER line from the `text`
 * stream? Those are the frames the promoted stream replaces when the flag is on.
 * Agent TTS and everything non-ORDERBOARD stay put — the promoted stream carries
 * neither.
 */
const isCustomerTextFinal = (frame: any): boolean =>
  frame?.agent_type === AgentTypes.orderBoard &&
  frame?.data?.status !== 'TTS' &&
  frame?.data?.status !== 'hypothesis';

/**
 * secondary_asr (HITL-1497): present a promoted event as a transcript frame.
 *
 * The renderer gates on `agent_type === ORDERBOARD` and branches on
 * `data.status`; a promoted event has neither, so it is normalized here and the
 * renderer stays dumb. Two deliberate choices:
 *
 * - `id` stays the PROMOTED event's id. `transcript_highlight` points its
 *   `source_text_event_id` at that id, so `entityAnnotationsMap[message.id]`
 *   resolves menu-item highlights with no join and no matching.
 * - `isPromoted` comes straight off `metadata.source` on the event itself —
 *   only a secondary-engine promotion is badge-worthy; primary-source commits
 *   are the baseline transcript and render as ordinary lines.
 *
 * No `metadata.second_lang_text`: the promoted event does not carry one, so
 * bilingual lines render primary-language text only while the flag is on.
 */
const promotedEventToFrame = (promoted: PromotedTextRef): any => ({
  id: promoted.id,
  event: EventTypes.text,
  timestamp: promoted.timestamp,
  agent_type: AgentTypes.orderBoard,
  data: { status: 'final', payload: promoted.text },
  isPromoted: promoted.source === 'secondary',
});

/**
 * Assemble one transcript list, swapping the customer-line source when
 * secondary ASR owns it.
 *
 * Flag OFF → today's behaviour exactly: every `text` frame, untouched.
 * Flag ON  → customer `text`/`final` frames are dropped and the promoted events
 * take their place. Agent TTS, HITL sends and the in-progress hypothesis are
 * unaffected. Sorting is on each event's own timestamp (a promotion lands a
 * little after the final it replaces; that ordering is accepted).
 */
const buildTranscript = (
  textFrames: any[],
  hypothesisFrames: any[],
  promotedEvents: PromotedTextRef[],
  secondaryAsrEnabled: boolean
): any[] => {
  if (!secondaryAsrEnabled) {
    return buildSortedTranscript(textFrames, hypothesisFrames);
  }
  const withoutCustomerFinals = textFrames.filter(
    (frame) => !isCustomerTextFinal(frame)
  );
  return buildSortedTranscript(
    [...withoutCustomerFinals, ...promotedEvents.map(promotedEventToFrame)],
    hypothesisFrames
  );
};

/**
 * The live transcript list: final + sent frames sorted by timestamp, with the
 * latest in-progress hypothesis appended. Memoized so it recomputes only when
 * the underlying frames change, not on every render of a consuming component.
 *
 * With `secondary_asr_enabled` on, customer lines come from `asr_promoted_text`
 * instead of `text`/`final` — see {@link buildTranscript}.
 */
const selectTranscriptMessages = createSelector(
  selectFinalTextFrames,
  selectSentTextFrames,
  selectInProgressHypothesisTextFrames,
  selectPromotedTextEvents,
  selectFeatureFlagSecondaryAsrEnabled,
  (
    finalFrames,
    sentFrames,
    hypothesisFrames,
    promotedEvents,
    secondaryAsrEnabled
  ) =>
    buildTranscript(
      [...finalFrames, ...sentFrames],
      hypothesisFrames,
      promotedEvents,
      secondaryAsrEnabled
    )
);

/**
 * The session-buffer (pre-HITL replay) transcript list, built the same way as
 * {@link selectTranscriptMessages} from the session-buffer frames.
 */
const selectSessionBufferTranscriptMessages = createSelector(
  selectSBFinalTextFrames,
  selectSBInProgressHypothesisTextFrames,
  selectSBPromotedTextEvents,
  selectFeatureFlagSecondaryAsrEnabled,
  (sbFinalFrames, sbHypothesisFrames, sbPromotedEvents, secondaryAsrEnabled) =>
    buildTranscript(
      [...sbFinalFrames],
      sbHypothesisFrames,
      sbPromotedEvents,
      secondaryAsrEnabled
    )
);

export {
  selectCurrentSessionId,
  selectFinalTextFrames,
  selectInProgressHypothesisTextFrames,
  selectIsAIActive,
  selectIsNewSession,
  selectIsPlaying,
  selectIsStaffIntervenedBeforeCarExit,
  selectIsStaffIntervention,
  selectIsWebSocketConnected,
  selectRestaurantStaffIntervention,
  selectSBFinalTextFrames,
  selectSBInProgressHypothesisTextFrames,
  selectSentTextFrames,
  selectIsCarPresent,
  selectEntityAnnotationsMap,
  selectPromotedTextEvents,
  selectSBPromotedTextEvents,
  selectAwaitingResponseSinceMs,
  selectAwaitingCustomerUtterance,
  selectTranscriptMessages,
  selectSessionBufferTranscriptMessages,
};
